/**
 * The supporting-material picker's rules, and the attorney's wording for them.
 * Kept out of JSX so a component renders a sentence rather than a comparison.
 * The API's slot table refuses the same things; this side is the pre-flight
 * affordance.
 */

import type { IntakeComponentSpec, SlotTag } from "@/lib/intake-components";

export type PendingStatus = "idle" | "sending" | "failed";

/* One file chosen but not yet added. Here rather than on the component that
   draws it, because a shared hook needs the shape and not the component. */
export interface PendingDescription {
  /** Stable while staged; a file name is not unique enough. */
  id: string;
  fileName: string;
  /** The browser's content type for the file, which decides the tags it can take. */
  contentType: string;
  /** The tag chosen so far, while its description is still being written. */
  tag: SlotTag["id"] | null;
  description: string;
  status: PendingStatus;
  /** What went wrong for this row alone, when its upload failed. */
  message?: string | null;
}

/** Whether a file with this tag and these words can be added: a tag that asks for words gets enough of them. */
export function canAdd(tag: SlotTag, description: string): boolean {
  return (
    tag.descriptionMin === null ||
    description.trim().length >= tag.descriptionMin
  );
}

export function descriptionHint(tag: SlotTag, description: string): string {
  return canAdd(tag, description)
    ? "Ready to add."
    : "A short description is needed before this file can be added.";
}

/**
 * The tags a file can be given. A tag that stores the file as another type
 * takes only that type's formats, so a spreadsheet is never offered "Figures".
 */
export function tagsFor(
  spec: IntakeComponentSpec,
  contentType: string,
): readonly SlotTag[] {
  return spec.tags.filter(
    (tag) => tag.fileType === spec.fileType || tag.takes.includes(contentType),
  );
}

/**
 * What the submit bar adds when files sit chosen but not added. Chosen files
 * are client-only, so continuing takes them with it, and the gate stays open
 * on purpose: this warning is all that stands in the way.
 */
export function stagedWarning(count: number): string {
  if (count <= 0) return "";
  const files =
    count === 1
      ? "1 supporting file has"
      : `${String(count)} supporting files have`;
  return `${files} been chosen but not added yet, and leaving now discards them.`;
}
